import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

const PartDetails = () => {
  const { id } = useParams();
  const { lang } = useLanguage();

  const [part, setPart] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form, setForm] = useState({
    name: "",
    surname: "",
    mobile: "",
    address: "",
  });

  // ================= TEXT =================
  const t = {
    sq: {
      whatsapp: "Përshëndetje",
      interested: "Jam i interesuar për këtë pjesë",
      part: "Pjesa",
      stock: "Në stok",
      outStock: "S’ka stok",
      reserve: "Rezervo Pjesën",
      name: "Emri",
      surname: "Mbiemri",
      mobile: "Tel",
      address: "Adresa",
      confirm: "Konfirmo",
      close: "Mbyll",
      send: "Duke u dërguar...",
      success: "✔ Rezervimi u dërgua me sukses!",
      error: "❌ Gabim gjatë rezervimit",
      fill: "Plotëso emrin dhe numrin e telefonit!",
      loading: "Duke u ngarkuar...",
    },
    en: {
      whatsapp: "Hello",
      interested: "I am interested in this part",
      part: "Part",
      stock: "In stock",
      outStock: "Out of stock",
      reserve: "Reserve Part",
      name: "Name",
      surname: "Surname",
      mobile: "Phone",
      address: "Address",
      confirm: "Confirm",
      close: "Close",
      send: "Sending...",
      success: "✔ Reservation sent successfully!",
      error: "❌ Error while reserving",
      fill: "Please fill name and phone number!",
      loading: "Loading...",
    },
  };

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    if (!id) return;

    const fetchPart = async () => {
      try {
        setFetching(true);

        const res = await fetch(`${API_BASE_URL}/api/parts/${id}`);
        const data = await res.json();

        setPart(data);
      } catch (err) {
        console.error(err);
      } finally {
        setFetching(false);
      }
    };

    fetchPart();
  }, [id]);

  // ================= SAFE STOCK =================
  const stock = Number(part?.stock ?? 0);

  // ================= FORM =================
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // ================= WHATSAPP =================
  const handleWhatsApp = () => {
    if (!part) return;

    const message = `
${text.whatsapp}

${text.interested}

📦 ${text.part}: ${part.name}
🚗 ${part.brand} ${part.model}
📂 ${part.category}
💰 €${Number(part.price || 0).toFixed(2)}

Faleminderit!
`;

    window.open(
      `https://wa.me/355688188188?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  // ================= RESERVE =================
  const handleReserve = async () => {
    if (!part) return;

    if (!form.name || !form.mobile) {
      alert(text.fill);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/orders/reserve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          email: null,
          product: {
            type: "part",
            id: part.id,
            name: part.name,
            brand: part.brand,
            model: part.model,
            category: part.category,
            price: part.price,
          },
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Error");

      alert(text.success);

      setForm({
        name: "",
        surname: "",
        mobile: "",
        address: "",
      });

      setMode(null);
    } catch (err) {
      console.error(err);
      alert(text.error);
    } finally {
      setLoading(false);
    }
  };

  // ================= LOADING =================
  if (fetching) {
    return (
      <div className="p-10 text-center text-gray-500">
        {text.loading}
      </div>
    );
  }

  if (!part) {
    return (
      <div className="p-10 text-center text-red-500">
        Error loading part
      </div>
    );
  }

 return (
  <main className="min-h-screen bg-slate-50 px-3 py-6 sm:px-5 lg:py-10">
    <div className="mx-auto w-full max-w-[1450px]">
      <div className="grid items-stretch gap-6 lg:grid-cols-[1.02fr_1fr]">

        {/* =====================================================
            LEFT — PART IMAGE
        ====================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="
            overflow-hidden
            rounded-[26px]
            border border-slate-200
            bg-white
            shadow-[0_18px_50px_rgba(15,23,42,0.08)]
          "
        >
          <div
            className="
              relative
              flex
              min-h-[500px]
              items-center
              justify-center
              overflow-hidden
              bg-gradient-to-br
              from-slate-50
              via-blue-50/50
              to-indigo-50
              p-8
            "
          >
            {/* SHADOW */}
            <div
              className="
                absolute
                left-1/2
                top-[76%]
                h-16
                w-[64%]
                -translate-x-1/2
                rounded-[50%]
                bg-slate-300/30
                blur-xl
              "
            />

            {/* PLATFORM */}
            <div
              className="
                absolute
                bottom-10
                left-1/2
                h-16
                w-[56%]
                -translate-x-1/2
                rounded-[50%]
                border border-slate-200
                bg-white
                shadow-[0_18px_30px_rgba(15,23,42,0.10)]
              "
            />

            <img
              src={
                part.image
                  ? `${API_BASE_URL}${part.image}`
                  : "https://placehold.co/500x500"
              }
              alt={part.name || "Pjesë këmbimi"}
              className="
                relative
                z-10
                h-[340px]
                w-full
                object-contain
                drop-shadow-[0_22px_20px_rgba(15,23,42,0.18)]
                transition
                duration-500
                hover:scale-105
                sm:h-[400px]
              "
            />
          </div>

          {/* PART SUMMARY */}
          <div className="border-t border-slate-100 p-5 sm:p-6">
            <span
              className="
                text-[11px]
                font-extrabold
                uppercase
                tracking-[0.12em]
                text-slate-400
              "
            >
              MASTER SOL&amp;TECH
            </span>

            <h3 className="mt-2 text-lg font-black text-slate-900">
              {part.name}
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              {[part.category, part.brand, part.model]
                .filter(Boolean)
                .join(" • ")}
            </p>
          </div>
        </motion.section>

        {/* =====================================================
            RIGHT — PART DETAILS
        ====================================================== */}
        <motion.section
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08 }}
          className="
            flex
            flex-col
            rounded-[26px]
            border border-slate-200
            bg-white
            p-6
            shadow-[0_18px_50px_rgba(15,23,42,0.08)]
            sm:p-8
          "
        >
          {/* CATEGORY BADGE */}
          <span
            className="
              w-fit
              rounded-full
              bg-violet-100
              px-3
              py-1.5
              text-xs
              font-extrabold
              text-violet-700
            "
          >
            {part.category || "Pjesë këmbimi"}
          </span>

          {/* TITLE */}
          <h1
            className="
              mt-4
              text-3xl
              font-black
              tracking-tight
              text-slate-950
              sm:text-4xl
            "
          >
            {part.name}
          </h1>

          <p className="mt-2 text-sm font-medium text-slate-500">
            {part.brand} • {part.model}
          </p>

          {/* PRICE + STOCK */}
          <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
            <p className="text-3xl font-black text-green-600">
              €{Number(part.price || 0).toFixed(2)}
            </p>

            {stock > 0 ? (
              <span
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  bg-green-50
                  px-3
                  py-2
                  text-sm
                  font-extrabold
                  text-green-700
                "
              >
                <span
                  className="
                    flex
                    h-5
                    w-5
                    items-center
                    justify-center
                    rounded-full
                    bg-green-500
                    text-xs
                    text-white
                  "
                >
                  ✓
                </span>

                {text.stock} ({stock})
              </span>
            ) : (
              <span
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  bg-red-50
                  px-3
                  py-2
                  text-sm
                  font-extrabold
                  text-red-700
                "
              >
                ✕ {text.outStock}
              </span>
            )}
          </div>

          <div className="my-6 h-px bg-slate-100" />

          {/* SPECIFICATIONS */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

            {/* CATEGORY */}
            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-layer-group" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.category}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {part.category || "-"}
                </p>
              </div>
            </div>

            {/* BRAND */}
            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-tag" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.brand}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {part.brand || "-"}
                </p>
              </div>
            </div>

            {/* MODEL */}
            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-mobile-alt" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.model}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {part.model || "-"}
                </p>
              </div>
            </div>

            {/* STOCK */}
            <div
              className="
                flex
                min-h-[86px]
                items-center
                gap-4
                rounded-2xl
                border border-slate-200
                bg-slate-50/70
                p-4
              "
            >
              <div
                className="
                  flex
                  h-10
                  w-10
                  flex-shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-indigo-100
                  text-indigo-700
                "
              >
                <i className="fas fa-boxes" />
              </div>

              <div>
                <span className="text-xs font-bold text-slate-500">
                  {text.stock}
                </span>

                <p className="mt-1 font-extrabold text-slate-900">
                  {stock > 0 ? stock : 0}
                </p>
              </div>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div
            className="
              mt-5
              rounded-2xl
              border border-slate-200
              bg-slate-50/70
              p-4
            "
          >
            <span
              className="
                text-[11px]
                font-extrabold
                uppercase
                tracking-[0.1em]
                text-violet-700
              "
            >
              Përshkrimi
            </span>

            <p className="mt-2 text-sm leading-6 text-slate-600">
              {part.description || "Nuk ka përshkrim për këtë pjesë."}
            </p>
          </div>

          {/* BUTTONS */}
          <div className="mt-auto grid grid-cols-1 gap-3 pt-7 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleWhatsApp}
              className="
                flex
                min-h-[52px]
                items-center
                justify-center
                gap-3
                rounded-xl
                bg-green-600
                py-3
                text-sm
                font-extrabold
                text-white
                shadow-[0_12px_25px_rgba(22,163,74,0.25)]
                transition
                hover:-translate-y-0.5
                hover:bg-green-700
              "
            >
              <i className="fab fa-whatsapp text-lg" />
              WhatsApp
            </button>

            <button
              type="button"
              onClick={() => setMode("reserve")}
              className="
                flex
                min-h-[52px]
                items-center
                justify-center
                gap-3
                rounded-xl
                bg-gradient-to-r
                from-violet-600
                to-fuchsia-600
                py-3
                text-sm
                font-extrabold
                text-white
                shadow-[0_12px_25px_rgba(124,58,237,0.25)]
                transition
                hover:-translate-y-0.5
                hover:from-violet-700
                hover:to-fuchsia-700
              "
            >
              <i className="fas fa-calendar-check" />
              {text.reserve}
            </button>
          </div>
        </motion.section>
      </div>
    </div>

    {/* =====================================================
        RESERVATION MODAL
    ====================================================== */}
    {mode === "reserve" && (
      <div
        className="
          fixed
          inset-0
          z-50
          flex
          items-center
          justify-center
          bg-slate-950/60
          p-4
          backdrop-blur-sm
        "
        onClick={() => setMode(null)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={(event) => event.stopPropagation()}
          className="
            relative
            w-full
            max-w-md
            rounded-3xl
            border border-slate-200
            bg-white
            p-6
            shadow-[0_30px_80px_rgba(15,23,42,0.30)]
          "
        >
          <button
            type="button"
            onClick={() => setMode(null)}
            className="
              absolute
              right-5
              top-5
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              bg-slate-100
              text-slate-600
              transition
              hover:bg-red-50
              hover:text-red-600
            "
          >
            ✕
          </button>

          <h2 className="pr-12 text-xl font-black text-slate-900">
            {text.reserve}
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            {part.name}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              name="name"
              placeholder={text.name}
              onChange={handleChange}
              className="
                h-11
                rounded-xl
                border border-slate-200
                bg-slate-50
                px-3
                text-sm
                outline-none
                transition
                focus:border-violet-500
                focus:bg-white
                focus:ring-4
                focus:ring-violet-100
              "
            />

            <input
              name="surname"
              placeholder={text.surname}
              onChange={handleChange}
              className="
                h-11
                rounded-xl
                border border-slate-200
                bg-slate-50
                px-3
                text-sm
                outline-none
                transition
                focus:border-violet-500
                focus:bg-white
                focus:ring-4
                focus:ring-violet-100
              "
            />
          </div>

          <input
            name="mobile"
            placeholder={text.mobile}
            onChange={handleChange}
            className="
              mt-3
              h-11
              w-full
              rounded-xl
              border border-slate-200
              bg-slate-50
              px-3
              text-sm
              outline-none
              transition
              focus:border-violet-500
              focus:bg-white
              focus:ring-4
              focus:ring-violet-100
            "
          />

          <textarea
            name="address"
            placeholder={text.address}
            onChange={handleChange}
            rows="4"
            className="
              mt-3
              w-full
              resize-none
              rounded-xl
              border border-slate-200
              bg-slate-50
              p-3
              text-sm
              outline-none
              transition
              focus:border-violet-500
              focus:bg-white
              focus:ring-4
              focus:ring-violet-100
            "
          />

          <button
            type="button"
            onClick={handleReserve}
            disabled={loading}
            className="
              mt-4
              min-h-[48px]
              w-full
              rounded-xl
              bg-gradient-to-r
              from-violet-600
              to-fuchsia-600
              text-sm
              font-extrabold
              text-white
              shadow-lg
              shadow-violet-200
              transition
              hover:from-violet-700
              hover:to-fuchsia-700
              disabled:cursor-not-allowed
              disabled:opacity-60
            "
          >
            {loading ? text.send : text.confirm}
          </button>

          <button
            type="button"
            onClick={() => setMode(null)}
            className="
              mt-3
              w-full
              py-2
              text-sm
              font-bold
              text-violet-700
              transition
              hover:text-violet-900
            "
          >
            {text.close}
          </button>
        </motion.div>
      </div>
    )}
  </main>
);
};

export default PartDetails;